import { logger } from '@tunasong/models'
import type { CSSProperties, FC, ReactNode } from 'react'
import { Component, useEffect, useState } from 'react'
import styles from './lazy-block.module.css'

/** The states `lazyBlock` renders around a block's code while it loads (GH #2408) */

/** A load this quick reads as instant, so nothing is said before it (Joni, GH #2408) */
const loadingDelayMs = 300

export interface PlaceholderProps {
  style: CSSProperties
  children?: ReactNode
}

/** The finished block's box, so nothing below it moves when the code lands */
export const Placeholder: FC<PlaceholderProps> = ({ style, children }) => (
  <div data-testid="lazy-block-placeholder" className={styles.placeholder} style={style}>
    {children}
  </div>
)

export const Loading: FC<{ style: CSSProperties }> = ({ style }) => {
  const [showCaption, setShowCaption] = useState(false)
  useEffect(() => {
    const timer = setTimeout(() => setShowCaption(true), loadingDelayMs)
    return () => clearTimeout(timer)
  }, [])
  return (
    <Placeholder style={style}>{showCaption ? <span className={styles.caption}>Loading…</span> : null}</Placeholder>
  )
}

export interface BlockBoundaryProps {
  children: ReactNode
  renderFailed(): ReactNode
}

/**
 * Catches a failed chunk for this block only: the block says it could not load, and the rest of the
 * document stays. React still needs a class for an error boundary.
 */
export class BlockBoundary extends Component<BlockBoundaryProps, { failed: boolean }> {
  state = { failed: false }

  static getDerivedStateFromError() {
    return { failed: true }
  }

  componentDidCatch(error: unknown) {
    logger.warn('lazyBlock: a block could not load', error)
  }

  render() {
    return this.state.failed ? this.props.renderFailed() : this.props.children
  }
}
